import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "./ui/card";

const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const BLN_PANJANG = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

// Rekap berapa kali tiap pelayan bertugas sepanjang tahun, dengan filter bulan (read-only, role terbatas di backend).
export const RekapBebanTahunan = ({ tahun }) => {
  const [data, setData] = useState(null);
  const [buka, setBuka] = useState(false);
  const [bulan, setBulan] = useState(0);
  useEffect(() => { if (buka) api.get("/jadwal/rekap-tahunan", { params: { tahun } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [tahun, buka]);
  const rows = useMemo(() => {
    if (!data) return [];
    if (!bulan) return data.pelayan;
    return data.pelayan.map((r) => ({ ...r, total: r.per_bulan[bulan - 1] })).filter((r) => r.total > 0).sort((a, b) => b.total - a.total || a.nama.localeCompare(b.nama));
  }, [data, bulan]);
  const maks = rows[0]?.total || 1;
  return (
    <Card className="rounded-2xl" data-testid="jadwal-rekap-tahunan">
      <button type="button" onClick={() => setBuka((b) => !b)} className="w-full p-4 flex items-center gap-2 text-left" data-testid="jadwal-rekap-tahunan-toggle">
        <BarChart3 className="h-4 w-4 text-[hsl(224,64%,33%)]" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Riwayat Beban Pelayan Tahun {tahun}</h2>
        <span className="text-xs text-slate-500 ml-auto">{data ? `${data.pelayan.length} pelayan · ${data.minggu_terisi} minggu terisi` : "Klik untuk melihat"}</span>
        {buka ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {buka && data && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-slate-600" htmlFor="rekap-bulan">Filter bulan</label>
            <select id="rekap-bulan" value={bulan} onChange={(e) => setBulan(Number(e.target.value))} className="min-h-[40px] rounded-xl border px-3 text-sm bg-white" data-testid="jadwal-rekap-filter-bulan">
              <option value={0}>Semua bulan ({tahun})</option>
              {BLN_PANJANG.map((b, i) => <option key={b} value={i + 1}>{b}</option>)}
            </select>
            <span className="text-xs text-slate-500" data-testid="jadwal-rekap-ringkas">{bulan ? `${rows.length} pelayan bertugas pada ${BLN_PANJANG[bulan - 1]}` : `${rows.length} pelayan sepanjang tahun`}</span>
          </div>
          <div className="overflow-x-auto">
            {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid="jadwal-rekap-tahunan-kosong">Belum ada jadwal terisi pada {bulan ? `${BLN_PANJANG[bulan - 1]} ` : ""}{tahun}.</p> : (
              <table className="w-full text-xs min-w-[720px]" data-testid="jadwal-rekap-tahunan-table">
                <thead className="text-slate-500">
                  <tr><th className="text-left p-1.5 sticky left-0 bg-white">Nama</th><th className="p-1.5 text-right">{bulan ? BLN[bulan - 1] : "Total"}</th>{BLN.map((b, i) => <th key={b} className={`p-1.5 text-center w-9 ${bulan === i + 1 ? "text-[hsl(224,64%,33%)] font-bold" : ""}`}>{b}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.nama} className="border-t" title={Object.entries(r.posisi).map(([p, n]) => `${p}: ${n}×`).join(", ")} data-testid={`jadwal-rekap-baris-${r.nama.replace(/\s+/g, "-")}`}>
                      <td className="p-1.5 font-medium text-slate-800 sticky left-0 bg-white whitespace-nowrap">
                        <div className="flex items-center gap-2">{r.nama}<span className="h-1.5 rounded-full bg-[hsl(224,64%,33%)]/70" style={{ width: `${Math.max(6, (r.total / maks) * 80)}px` }} /></div>
                      </td>
                      <td className="p-1.5 text-right font-bold text-slate-900">{r.total}×</td>
                      {r.per_bulan.map((n, i) => <td key={i} className={`p-1.5 text-center ${bulan === i + 1 ? "bg-blue-50" : ""} ${n ? "text-slate-800" : "text-slate-300"}`}>{n || "·"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-slate-400">Dihitung dari jadwal tersimpan. Arahkan kursor ke baris untuk melihat rincian posisi. Hanya untuk Admin, Pimpinan, Sekretariat, Koordinator Ibadah, dan Majelis Penatalayanan.</p>
        </div>
      )}
    </Card>
  );
};
